import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { FiTrendingUp, FiCalendar, FiBarChart2, FiHeart, FiTarget, FiZap, FiInfo } from "react-icons/fi";
import { getZoneColor, formatZoneName } from "../../utils/zoneUtils";

// TypeScript interfaces
interface OverviewStats {
  totalMoods: number;
  averageImpact: number;
  averageJoyfulness: number;
  mostCommonZone: string;
  mostCommonEmotion: string;
  streakDays: number;
  totalDays: number;
}

interface ZoneDistribution {
  zone: string;
  count: number;
  percentage: number;
  color: string;
}

interface EmotionDistribution {
  emotion: string;
  count: number;
  percentage: number;
  icon: string;
  color: string;
}

interface WeeklyTrend {
  day: string;
  moods: number;
  avgImpact: number;
  avgJoyfulness: number;
}

interface Insight {
  type: "positive" | "negative" | "info";
  title: string;
  message: string;
  icon: string;
}

const MoodOverview: React.FC = () => {

  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<"week" | "month" | "year">("month");
  const [loading, setLoading] = useState(true);
  
  const [overviewStats, setOverviewStats] = useState<OverviewStats>({
    totalMoods: 0,
    averageImpact: 0,
    averageJoyfulness: 0,
    mostCommonZone: "None",
    mostCommonEmotion: "None",
    streakDays: 0,
    totalDays: 0,
  });

  const [zoneDistribution, setZoneDistribution] = useState<ZoneDistribution[]>([]);
  const [emotionDistribution, setEmotionDistribution] = useState<EmotionDistribution[]>([]);
  const [weeklyTrend, setWeeklyTrend] = useState<WeeklyTrend[]>([]);
  const [insights, setInsights] = useState<Insight[]>([]);

  useEffect(() => {
    const fetchOverview = async () => {
      if (!user?.id) return;
      try {
        setLoading(true);
        const today = new Date();
        const dateFrom = new Date();
        switch (selectedPeriod) {
          case "week": dateFrom.setDate(today.getDate() - 7); break;
          case "month": dateFrom.setMonth(today.getMonth() - 1); break;
          case "year": dateFrom.setFullYear(today.getFullYear() - 1); break;
        }

        const res = await axios.get(`${API_BASE_URL}/student-mood-logs`, {
          params: { 
            studentId: user.id, 
            status: true,
            dateFrom: dateFrom.toISOString().split('T')[0],
            dateTo: today.toISOString().split('T')[0]
          },
        });
        
        const logs = res.data || [];
        
        let totalImpact = 0;
        let totalJoy = 0;
        const zoneCounts: Record<string, number> = {};
        const emotionCounts: Record<string, number> = {};
        
        logs.forEach((log: any) => {
          totalImpact += log.impact || 0;
          totalJoy += log.joyfulness || 0;
          
          const zone = log.calculatedZone || "Unknown Zone";
          zoneCounts[zone] = (zoneCounts[zone] || 0) + 1;
          
          const emotion = log.emotion || "Unknown";
          emotionCounts[emotion] = (emotionCounts[emotion] || 0) + 1;
        });

        const total = logs.length;
        const mostCommonZone = Object.keys(zoneCounts).sort((a, b) => zoneCounts[b] - zoneCounts[a])[0] || "None";
        const mostCommonEmotion = Object.keys(emotionCounts).sort((a, b) => emotionCounts[b] - emotionCounts[a])[0] || "None";
        
        setOverviewStats({
          totalMoods: total,
          averageImpact: total > 0 ? totalImpact / total : 0,
          averageJoyfulness: total > 0 ? totalJoy / total : 0,
          mostCommonZone,
          mostCommonEmotion,
          streakDays: total > 0 ? 1 : 0,
          totalDays: 30,
        });

        const zd: ZoneDistribution[] = Object.keys(zoneCounts).map(z => ({
          zone: z,
          count: zoneCounts[z],
          percentage: Math.round((zoneCounts[z] / total) * 100),
          color: getZoneColor(formatZoneName(z)).color,
        }));
        setZoneDistribution(zd.sort((a,b) => b.count - a.count));

        // Emotion Distribution — icon field removed; EmotionIcon component used in render
        const ed: EmotionDistribution[] = Object.keys(emotionCounts).map(e => ({
          emotion: e,
          count: emotionCounts[e],
          percentage: Math.round((emotionCounts[e] / total) * 100),
          icon: "",   // kept for interface compatibility; not rendered as emoji
          color: "#2ecc71"
        }));
        setEmotionDistribution(ed.sort((a,b) => b.count - a.count).slice(0, 5));

        // Insights (Simple pseudo-insights)
        setInsights([
          { type: "info", title: "Activity", message: `You logged ${total} moods this ${selectedPeriod}.`, icon: "info" }
        ]);

      } catch (err) {
        console.error("Error fetching mood overview data", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOverview();
  }, [user, selectedPeriod]);

  if (loading) return <div className="p-8 text-center">Loading overview...</div>;


  return (
    <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-slate-200/80 flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold m-0">Mood Overview</h2>
          <p className="text-gray-500 mt-1.5 text-sm">
            Comprehensive insights into your emotional patterns
          </p>
        </div>
        <div className="flex gap-2">
          {(["week", "month", "year"] as const).map((period) => (
            <button
              key={period}
              className={`px-4 py-2 rounded-lg font-medium transition-colors capitalize ${
                selectedPeriod === period
                  ? "bg-[#1ecab8] text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              onClick={() => setSelectedPeriod(period)}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
          <div className="flex justify-between items-start mb-4">
            <span className="text-sm font-semibold text-slate-600">
              Total Moods
            </span>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-100">
              <FiBarChart2 className="text-blue-600" />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">
            {overviewStats.totalMoods}
          </div>
          <div className="text-xs text-slate-500">
            logged this {selectedPeriod}
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
          <div className="flex justify-between items-start mb-4">
            <span className="text-sm font-semibold text-slate-600">
              Avg Impact
            </span>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-green-100">
              <FiTrendingUp className="text-green-600" />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">
            {overviewStats.averageImpact.toFixed(1)}
          </div>
          <div className="text-xs text-slate-500">out of 7</div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
          <div className="flex justify-between items-start mb-4">
            <span className="text-sm font-semibold text-slate-600">
              Avg Joyfulness
            </span>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-yellow-100">
              <FiHeart className="text-yellow-600" size={22} aria-label="Joyfulness" />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">
            {overviewStats.averageJoyfulness.toFixed(1)}
          </div>
          <div className="text-xs text-slate-500">out of 7</div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80">
          <div className="flex justify-between items-start mb-4">
            <span className="text-sm font-semibold text-slate-600">
              Most Common Zone
            </span>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-purple-100">
              <FiTarget className="text-purple-600" size={22} aria-label="Most Common Zone" />
            </div>
          </div>
          <div className="text-xl font-bold text-slate-900 mb-1">
            {overviewStats.mostCommonZone}
          </div>
          <div className="text-xs text-slate-500">this {selectedPeriod}</div>
        </div>
      </div>

      {/* Zone Distribution */}
      <div className="bg-white rounded-xl p-7 mb-6 shadow-sm border border-slate-200/80">
        <div className="flex items-center gap-2 text-lg font-semibold text-slate-900 mb-6">
          <FiBarChart2 />
          Zone Distribution
        </div>
        <div className="space-y-4">
          {zoneDistribution.map((item) => (
            <div key={item.zone}>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ background: item.color }}
                  />
                  <span className="font-semibold text-sm">{item.zone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">
                    {item.count} moods
                  </span>
                  <span
                    className="font-bold text-base"
                    style={{ color: item.color }}
                  >
                    {item.percentage}%
                  </span>
                </div>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${item.percentage}%`,
                    background: item.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Emotion Distribution */}
      <div className="bg-white rounded-xl p-7 mb-6 shadow-sm border border-slate-200/80">
        <div className="flex items-center gap-2 text-lg font-semibold text-slate-900 mb-6">
          <FiTrendingUp />
          Emotion Distribution
        </div>
        <div className="space-y-4">
          {emotionDistribution.map((item) => (
            <div key={item.emotion}>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-3">
                  {/* colored dot for emotion — consistent across all platforms */}
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: `${item.color}20` }}>
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
                  </div>
                  <span className="font-semibold text-sm">{item.emotion}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">
                    {item.count} times
                  </span>
                  <span
                    className="font-bold text-base"
                    style={{ color: item.color }}
                  >
                    {item.percentage}%
                  </span>
                </div>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${item.percentage}%`,
                    background: item.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly Trend */}
      <div className="bg-white rounded-xl p-7 mb-6 shadow-sm border border-slate-200/80">
        <div className="flex items-center gap-2 text-lg font-semibold text-slate-900 mb-6">
          <FiCalendar />
          Weekly Trend
        </div>
        <div className="grid grid-cols-7 gap-3">
          {weeklyTrend.map((day) => (
            <div
              key={day.day}
              className="text-center p-4 bg-gray-50 rounded-lg"
            >
              <div className="text-xs text-gray-500 mb-2">{day.day}</div>
              <div className="text-xl font-bold mb-1">{day.moods}</div>
              <div className="text-[11px] text-gray-600">moods</div>
              <div className="mt-2 p-1.5 bg-white rounded-md text-[11px]">
                <div>I: {day.avgImpact}</div>
                <div>J: {day.avgJoyfulness}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Insights */}
      <div className="bg-white rounded-xl p-7 shadow-sm border border-slate-200/80">
        <div className="flex items-center gap-2 text-lg font-semibold text-slate-900 mb-6">
          <FiZap size={18} className="text-amber-500" aria-hidden />
          Insights
        </div>
        <div className="space-y-4">
          {insights.map((insight, index) => (
            <div
              key={index}
              className={`p-5 rounded-xl flex items-center gap-4 ${
                insight.type === "positive"
                  ? "bg-blue-50 border-2 border-[#1ecab8]"
                  : insight.type === "negative"
                  ? "bg-red-50 border-2 border-red-500"
                  : "bg-gray-50 border-2 border-gray-300"
              }`}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: insight.type === "positive" ? "#d1fae5" : insight.type === "negative" ? "#fee2e2" : "#dbeafe"
                }}
              >
                <FiInfo
                  size={18}
                  aria-label={insight.title}
                  style={{ color: insight.type === "positive" ? "#059669" : insight.type === "negative" ? "#dc2626" : "#2563eb" }}
                />
              </div>
              <div className="flex-1">
                <div className="text-base font-bold mb-1 text-slate-900">
                  {insight.title}
                </div>
                <div className="text-sm text-gray-600 leading-relaxed">
                  {insight.message}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MoodOverview;

